import React, { useState, useEffect, useRef } from "react";
import Loader from "../../Loader";
import { niceTicks } from "../chartMath.js";

const INTERVALS = { "1a": "1A", "2a": "2A", "5a": "5A", "10a": "10A", "max": "MAX" };

// Nombre de paliers visés sur l'axe des valeurs en plein écran (configurable : 10, 15, 20…)
const Y_AXIS_TICK_COUNT = 10;

const ExpandIcon = () => (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="none">
    <path d="M9 3H4v5M15 3h5v5M9 21H4v-5M15 21h5v-5" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
  </svg>
);

const StockChart = ({ marcheId, leagueid, onDataLoad, large = false }) => {
  const [series, setSeries]               = useState([]);
  const [loading, setLoading]             = useState(false);
  const [selectedInterval, setInterval]   = useState("1a");
  const [hoverIdx, setHoverIdx]           = useState(null);
  const [mousePos, setMousePos]           = useState({ x: 0, y: 0 });
  const [measured, setMeasured]           = useState({ w: 900, h: 420 });
  const [domain, setDomain]               = useState([0, 0]);
  const [isDragging, setIsDragging]       = useState(false);
  const svgRef = useRef(null);
  const chartWrapRef = useRef(null);
  const zoomParamsRef = useRef({ W: 400, iw: 380, total: 0 });
  const zoomAtRef = useRef(() => {});
  const panByRef = useRef(() => {});

  // Réinitialise le zoom à chaque nouveau jeu de données (changement d'intervalle/marché)
  useEffect(() => {
    setDomain([0, Math.max(series.length - 1, 0)]);
    setHoverIdx(null);
  }, [series]);

  useEffect(() => {
    if (!large || !chartWrapRef.current) return;
    const el = chartWrapRef.current;
    const update = () => {
      const rect = el.getBoundingClientRect();
      if (rect.width > 0 && rect.height > 0) setMeasured({ w: rect.width, h: rect.height });
    };
    update();
    const ro = new ResizeObserver(update);
    ro.observe(el);
    return () => ro.disconnect();
  }, [large]);

  const W = large ? measured.w : 400;
  const H = large ? measured.h : 150;
  const PAD = large
    ? { top: 10, right: 62, bottom: 32, left: 10 }
    : { top: 10, right: 10, bottom: 26, left: 10 };
  const iw = W - PAD.left - PAD.right;
  const ih = H - PAD.top - PAD.bottom;

  const API_URL = process.env.REACT_APP_API_URL + '/api';

  const fetchData = async (interval) => {
    setLoading(true);
    try {
      const res = await fetch(`${API_URL}/getDatabayPeriodvertuelbougiev2/${marcheId}/${leagueid}/${interval}`);
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const data = await res.json();
      if (data.values?.length > 0) {
        const processed = data.values.map(item => ({
          x:     new Date(item.x),
          open:  parseFloat(item.open.toFixed(2)),
          high:  parseFloat(item.high.toFixed(2)),
          low:   parseFloat(item.low.toFixed(2)),
          close: parseFloat(item.close.toFixed(2)),
        }));
        setSeries(processed);
        if (onDataLoad && processed.length > 0) {
          const closes = processed.map(d => d.close);
          onDataLoad({
            closes,
            firstClose: closes[0],
            lastClose:  closes.at(-1),
            high:       Math.max(...closes),
            low:        Math.min(...closes),
          });
        }
      } else {
        setSeries([]);
      }
    } catch (e) {
      console.error(e);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchData(selectedInterval); }, [selectedInterval, marcheId, leagueid]);

  // ── Zoom : fenêtre visible sur la série ───────────────────────
  const domStart = large ? Math.min(domain[0], domain[1]) : 0;
  const domEnd   = large ? Math.max(domain[0], domain[1]) : Math.max(series.length - 1, 0);
  const visibleSeries = large ? series.slice(domStart, domEnd + 1) : series;

  // ── Géométrie ──────────────────────────────────────────────
  const closes  = visibleSeries.map(d => d.close);
  const minVal  = closes.length ? Math.min(...closes) : 0;
  const maxVal  = closes.length ? Math.max(...closes) : 1;
  const rangeVal = maxVal - minVal;
  // Si la donnée est plate (aucune variation), on garde une marge symétrique autour de la
  // valeur unique pour que la ligne s'affiche au milieu du graphe, pas collée en bas.
  const vPad    = rangeVal > 0 ? rangeVal * 0.06 : (Math.abs(maxVal) || 1) * 0.05;
  const domainMin = minVal - vPad;
  const domainMax = maxVal + vPad;
  const domainSpan = domainMax - domainMin;

  const scaleX = (i) => PAD.left + (visibleSeries.length > 1 ? (i / (visibleSeries.length - 1)) * iw : iw / 2);
  const scaleY = (v) => PAD.top + ih - ((v - domainMin) / domainSpan) * ih;

  // Paliers de valeurs (axe Y, plein écran) : pas arrondi + décimales déduites du pas
  const { ticks: yTicks, step: yStep } = large
    ? niceTicks(domainMin, domainMax, Y_AXIS_TICK_COUNT)
    : { ticks: [], step: 1 };
  const yDecimals = Math.max(0, Math.ceil(-Math.log10(yStep || 1)));
  const yMidTarget = (minVal + maxVal) / 2;
  const yMidTick = yTicks.length
    ? yTicks.reduce((best, v) => Math.abs(v - yMidTarget) < Math.abs(best - yMidTarget) ? v : best, yTicks[0])
    : null;

  const pts      = visibleSeries.map((d, i) => [scaleX(i), scaleY(d.close)]);
  const line     = pts.map((p, i) => `${i ? 'L' : 'M'}${p[0].toFixed(1)},${p[1].toFixed(1)}`).join(' ');
  const area     = pts.length > 1
    ? `${line} L${pts.at(-1)[0].toFixed(1)},${(PAD.top + ih).toFixed(1)} L${pts[0][0].toFixed(1)},${(PAD.top + ih).toFixed(1)} Z`
    : '';

  const isUp  = visibleSeries.length > 1 ? visibleSeries.at(-1).close >= visibleSeries[0].close : true;
  const color = isUp ? '#4ADE9E' : '#FF7A78';
  const last  = pts.at(-1) ?? null;
  const gradId = `sg-${marcheId}`;

  // Labels X : 4 points répartis sur la fenêtre visible
  const MONTHS = ["Jan","Fév","Mar","Avr","Mai","Jun","Jul","Aoû","Sep","Oct","Nov","Déc"];
  const formatXLabel = (d) => `${MONTHS[d.getMonth()]} ${d.getFullYear()}`;
  const xLabels = (() => {
    if (visibleSeries.length < 2) return [];
    const rawPicks = [0, Math.floor(visibleSeries.length / 3), Math.floor((2 * visibleSeries.length) / 3), visibleSeries.length - 1];
    // Dédoublonne : avec peu de points, plusieurs quartiles peuvent tomber sur le même
    // index et empiler les labels les uns sur les autres.
    const picks = [...new Set(rawPicks)];
    return picks.map((idx, i) => ({
      x: scaleX(idx),
      label: formatXLabel(visibleSeries[idx].x),
      anchor: i === 0 ? 'start' : i === picks.length - 1 ? 'end' : 'middle',
    }));
  })();

  // Zoom molette / pincement : centré sur le point sous le curseur / entre les doigts
  zoomParamsRef.current = { W, iw, total: Math.max(series.length - 1, 0) };
  zoomAtRef.current = (px, rectWidth, scale) => {
    const { W: w, iw: ivw, total } = zoomParamsRef.current;
    if (total <= 0 || !rectWidth) return;
    setDomain(([start, end]) => {
      const svgX = (px / rectWidth) * w;
      const anchorFrac = Math.min(1, Math.max(0, (svgX - PAD.left) / ivw));
      const span = end - start;
      const anchorIdx = start + anchorFrac * span;
      const minSpan = Math.min(4, total);
      let newSpan = Math.min(total, Math.max(minSpan, span / scale));
      let newStart = anchorIdx - anchorFrac * newSpan;
      let newEnd = newStart + newSpan;
      if (newStart < 0) { newEnd -= newStart; newStart = 0; }
      if (newEnd > total) { newStart -= (newEnd - total); newEnd = total; }
      newStart = Math.max(0, newStart);
      return [Math.round(newStart), Math.round(newEnd)];
    });
    setHoverIdx(null);
  };

  // Déplacement (pan) au clic-glisser, une fois zoomé
  panByRef.current = (deltaPx, rectWidth) => {
    const { W: w, iw: ivw, total } = zoomParamsRef.current;
    if (!rectWidth || total <= 0) return;
    setDomain(([start, end]) => {
      const span = end - start;
      if (span >= total) return [start, end];
      const deltaSvg = (deltaPx / rectWidth) * w;
      const deltaIdx = (deltaSvg / ivw) * span;
      let newStart = start - deltaIdx;
      let newEnd = end - deltaIdx;
      if (newStart < 0) { newEnd -= newStart; newStart = 0; }
      if (newEnd > total) { newStart -= (newEnd - total); newEnd = total; }
      return [Math.max(0, newStart), Math.min(total, newEnd)];
    });
  };

  const handleWheel = (e) => {
    if (!large || !svgRef.current) return;
    e.preventDefault();
    const rect = svgRef.current.getBoundingClientRect();
    const scale = e.deltaY < 0 ? 1.2 : 1 / 1.2;
    zoomAtRef.current(e.clientX - rect.left, rect.width, scale);
  };

  const handleDoubleClick = () => {
    if (!large) return;
    setDomain([0, Math.max(series.length - 1, 0)]);
    setHoverIdx(null);
  };

  // Pincement tactile (mobile/tablette) : attaché nativement pour pouvoir bloquer le scroll de page
  useEffect(() => {
    if (!large || !svgRef.current) return;
    const el = svgRef.current;
    let lastDist = null;
    let lastTapTime = 0;

    const dist = (t) => Math.hypot(t[1].clientX - t[0].clientX, t[1].clientY - t[0].clientY);
    const midX = (t, rect) => ((t[0].clientX + t[1].clientX) / 2) - rect.left;

    const onTouchStart = (e) => {
      if (e.touches.length === 2) {
        lastDist = dist(e.touches);
      } else if (e.touches.length === 1) {
        const now = Date.now();
        if (now - lastTapTime < 300) {
          setDomain([0, zoomParamsRef.current.total]);
          setHoverIdx(null);
        }
        lastTapTime = now;
      }
    };
    const onTouchMove = (e) => {
      if (e.touches.length === 2 && lastDist != null) {
        e.preventDefault();
        const rect = el.getBoundingClientRect();
        const d = dist(e.touches);
        const scale = d / lastDist;
        lastDist = d;
        zoomAtRef.current(midX(e.touches, rect), rect.width, scale);
      }
    };
    const onTouchEnd = (e) => {
      if (e.touches.length < 2) lastDist = null;
    };

    el.addEventListener('touchstart', onTouchStart, { passive: true });
    el.addEventListener('touchmove', onTouchMove, { passive: false });
    el.addEventListener('touchend', onTouchEnd, { passive: true });
    return () => {
      el.removeEventListener('touchstart', onTouchStart);
      el.removeEventListener('touchmove', onTouchMove);
      el.removeEventListener('touchend', onTouchEnd);
    };
  }, [large]);

  // Déplacement au clic gauche maintenu + glisser
  useEffect(() => {
    if (!large || !svgRef.current) return;
    const el = svgRef.current;
    let dragging = false;
    let lastX = 0;

    const onMouseDown = (e) => {
      if (e.button !== 0) return;
      dragging = true;
      lastX = e.clientX;
      setIsDragging(true);
    };
    const onMouseMove = (e) => {
      if (!dragging) return;
      const rect = el.getBoundingClientRect();
      const deltaPx = e.clientX - lastX;
      lastX = e.clientX;
      panByRef.current(deltaPx, rect.width);
    };
    const onMouseUp = () => {
      if (!dragging) return;
      dragging = false;
      setIsDragging(false);
    };

    el.addEventListener('mousedown', onMouseDown);
    window.addEventListener('mousemove', onMouseMove);
    window.addEventListener('mouseup', onMouseUp);
    return () => {
      el.removeEventListener('mousedown', onMouseDown);
      window.removeEventListener('mousemove', onMouseMove);
      window.removeEventListener('mouseup', onMouseUp);
    };
  }, [large]);

  // Hover
  const handleMouseMove = (e) => {
    if (!svgRef.current || visibleSeries.length === 0) return;
    const rect = svgRef.current.getBoundingClientRect();
    const px = e.clientX - rect.left;
    const py = e.clientY - rect.top;
    setMousePos({ x: px, y: py });
    const svgX = (px / rect.width) * W;
    const idx  = Math.min(visibleSeries.length - 1, Math.max(0,
      Math.round(((svgX - PAD.left) / iw) * (visibleSeries.length - 1))
    ));
    setHoverIdx(idx);
  };

  const hoverPt   = hoverIdx != null && pts.length > 0  ? pts[hoverIdx]    : null;
  const hoverData = hoverIdx != null && visibleSeries.length > 0 ? visibleSeries[hoverIdx] : null;

  return (
    <div style={large ? { display: 'flex', flexDirection: 'column', height: '100%' } : undefined}>
      {/* ── Chart ──────────────────────────────────── */}
      <div ref={chartWrapRef} style={large
        ? { position: 'relative', overflow: 'hidden', flex: 1, minHeight: 0 }
        : { position: 'relative', borderRadius: 12, overflow: 'hidden' }
      }>
        {loading && (
          <div style={{
            position: 'absolute', inset: 0, zIndex: 1,
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            background: 'rgba(10,18,38,0.4)',
          }}>
            <Loader size={28} />
          </div>
        )}

        <svg
          ref={svgRef}
          width="100%" height={large ? '100%' : undefined} viewBox={`0 0 ${W} ${H}`}
          style={{
            display: 'block', opacity: loading ? 0.3 : 1, transition: 'opacity 0.2s',
            cursor: isDragging ? 'grabbing' : 'crosshair',
            touchAction: large ? 'none' : undefined,
          }}
          onMouseMove={handleMouseMove}
          onMouseLeave={() => setHoverIdx(null)}
          onWheel={handleWheel}
          onDoubleClick={handleDoubleClick}
        >
          <defs>
            <linearGradient id={gradId} x1="0" y1="0" x2="0" y2="1">
              <stop offset="0%"   stopColor={color} stopOpacity="0.20"/>
              <stop offset="100%" stopColor={color} stopOpacity="0"/>
            </linearGradient>
          </defs>

          {/* Grille horizontale subtile */}
          {large
            ? yTicks.map((val) => {
                const y = scaleY(val);
                if (y < PAD.top - 0.5 || y > PAD.top + ih + 0.5) return null;
                return (
                  <line key={val}
                    x1={PAD.left} x2={W - PAD.right}
                    y1={y} y2={y}
                    stroke="rgba(255,255,255,0.04)" strokeWidth="1"
                  />
                );
              })
            : [0.25, 0.5, 0.75].map((r, i) => (
                <line key={i}
                  x1={PAD.left} x2={W - PAD.right}
                  y1={PAD.top + ih * (1 - r)} y2={PAD.top + ih * (1 - r)}
                  stroke="rgba(255,255,255,0.04)" strokeWidth="1"
                />
              ))
          }

          {/* Aire */}
          {area && <path d={area} fill={`url(#${gradId})`}/>}

          {/* Ligne */}
          {line && (
            <path d={line} fill="none" stroke={color}
              strokeWidth="1.6" strokeLinejoin="round" strokeLinecap="round"/>
          )}

          {/* Ligne de survol */}
          {hoverPt && (
            <line
              x1={hoverPt[0]} x2={hoverPt[0]}
              y1={PAD.top} y2={PAD.top + ih}
              stroke="rgba(255,255,255,0.14)" strokeWidth="1"
            />
          )}

          {/* Point actif */}
          {(hoverPt ?? last) && (() => {
            const [cx, cy] = hoverPt ?? last;
            return (
              <>
                <circle cx={cx} cy={cy} r="3"   fill={color}/>
                <circle cx={cx} cy={cy} r="5.5" fill={color} opacity="0.2"/>
              </>
            );
          })()}

          {/* Labels axe X */}
          {xLabels.map((l, i) => (
            <text key={i} x={l.x} y={large ? H - 8 : H - 5}
              textAnchor={l.anchor} fill="#5C6B88"
              fontSize={large ? 13 : 9.5} fontFamily="inherit">
              {l.label}
            </text>
          ))}
        </svg>

        {/* Labels axe Y (plein écran) — paliers arrondis, palier central mis en évidence */}
        {large && yTicks.map((val) => {
          const y = scaleY(val);
          if (y < PAD.top - 0.5 || y > PAD.top + ih + 0.5) return null;
          const isMid = val === yMidTick;
          return (
            <div key={val} style={{
              position: 'absolute', right: 6, top: y, transform: 'translateY(-50%)',
              fontSize: 13, fontWeight: 700, fontVariantNumeric: 'tabular-nums',
              whiteSpace: 'nowrap', pointerEvents: 'none',
              color: isMid ? '#0A1226' : '#97A4BE',
              background: isMid ? '#F2F5FB' : 'transparent',
              border: isMid ? '1.5px solid #FFFFFF' : 'none',
              borderRadius: isMid ? 6 : 0,
              padding: isMid ? '3px 7px' : 0,
            }}>
              {val.toLocaleString('fr-FR', { minimumFractionDigits: yDecimals, maximumFractionDigits: yDecimals })}
            </div>
          );
        })}

        {/* Tooltip hover OHLC */}
        {hoverData && !isDragging && (
          <div style={{
            position: 'absolute',
            ...(large
              ? {
                  ...(mousePos.x > W - 150 ? { right: W - mousePos.x + 16 } : { left: mousePos.x + 16 }),
                  ...(mousePos.y > H - 100 ? { bottom: H - mousePos.y + 16 } : { top: mousePos.y + 16 }),
                }
              : {
                  top: 8,
                  ...(hoverIdx < series.length / 2 ? { right: 10 } : { left: 10 }),
                }
            ),
            background: 'rgba(10,18,38,0.88)',
            backdropFilter: 'blur(10px)',
            WebkitBackdropFilter: 'blur(10px)',
            border: '1px solid rgba(255,255,255,0.08)',
            borderRadius: 10, padding: '6px 10px',
            pointerEvents: 'none',
          }}>
            <div style={{ fontSize: 10, color: '#5C6B88', marginBottom: 4 }}>
              {hoverData.x.toLocaleDateString('fr-FR', { day: 'numeric', month: 'short', year: 'numeric' })}
            </div>
            {[['O', hoverData.open], ['H', hoverData.high], ['L', hoverData.low], ['C', hoverData.close]].map(([lbl, val]) => (
              <div key={lbl} style={{ display: 'flex', gap: 6, fontSize: 11, lineHeight: 1.6 }}>
                <span style={{ color: '#5C6B88', width: 10 }}>{lbl}</span>
                <span style={{ color: '#F2F5FB', fontWeight: 600, fontVariantNumeric: 'tabular-nums' }}>
                  {val.toLocaleString('fr-FR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                </span>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* ── Sélecteur d'intervalle ───────────────── */}
      <div style={{ display: 'flex', alignItems: 'center', gap: large ? 8 : 4, paddingTop: large ? 14 : 6 }}>
        {Object.entries(INTERVALS).map(([key, label]) => (
          <button
            key={key}
            onClick={() => setInterval(key)}
            style={{
              flex: 1, padding: large ? '13px 0' : '7px 0',
              fontSize: large ? 15 : 11, fontWeight: 600, borderRadius: large ? 10 : 8,
              border: 'none', cursor: 'pointer',
              background: selectedInterval === key ? 'rgba(255,255,255,0.08)' : 'transparent',
              color:      selectedInterval === key ? '#F2F5FB' : '#5C6B88',
              transition: 'color 0.1s, background 0.1s',
            }}
          >{label}</button>
        ))}
        {!large && (
          <button
            onClick={() => window.open(`/full_screen/${marcheId}/${leagueid}`, '_blank', 'noopener')}
            title="Ouvrir en plein écran"
            style={{
              flexShrink: 0, width: 35, height: 35,
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              borderRadius: 8, cursor: 'pointer',
              background: 'transparent', color: '#5C6B88',
            }}
          >
            <ExpandIcon/>
          </button>
        )}
      </div>
    </div>
  );
};

export default StockChart;
